<script setup lang="ts">
import { reactive, watch, ref } from 'vue';
import type { BaseTextElement, BoundedTextElement, FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import CSlider from '@/components/shared/CSlider.vue';
import { ElementMapOption } from './types';
import { useColor } from '@/composables/color';
import { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { KeyValueDto } from '@/typings/keyValueDto';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import { ShadowTypeEnum } from '@/utils/enums/shadowType.enum';

const disabled = ref(false);
const model = reactive({
    shadowOffset: 0,
    shadowAngle: 0,
    shadowBlur: 0,
    shadowType: null as ShadowTypeEnum | null,
    shadowColorLibraryId: undefined as number | undefined,
    shadowColorOption: undefined as number | undefined,
    shadowColorsMap: new Map<number, FontColorOption>(),
});

const COLOR_TYPE = LibraryTypeEnum.Color;
const DROP_SHADOW = ShadowTypeEnum.Drop;
const BLURRED_SHADOW = ShadowTypeEnum.Blurred;
type TextElement = BoundedTextElement;

const defaultOffset = 50;
const defaultAngle = 45;
const defaultBlur = 20;

const shadowOptions = [
    {
        type: ShadowTypeEnum.Drop,
        img: require('@/assets/images/drop-shadow.svg'),
        alt: 'drop-shadow'
    }
    //Not supported for V1:
    // {
    //     type: ShadowTypeEnum.Blurred,
    //     img: require('@/assets/images/blurred-drop-shadow.svg'),
    //     alt: 'blurred-drop-shadow'
    // }
];

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        textElement?: TextElement;
        editMode?: boolean;
        cmykMode?: boolean;
        isPreview?: boolean;
        exportFormat?:string;
    }>(),
    {
        textElement: undefined,
        isPreview: false
    }
);

const emit = defineEmits<{
    (e: 'shadow-change'): void;
    (e: 'open-shadow-color-library-selector'): void;
}>();

watch(
    () => props.textElement,
    (element) => {
        if (element?.type != 'bounded-text') return;

        let exportFormatDisabled = false;
        if (props.exportFormat) {
            exportFormatDisabled = props.exportFormat != 'png' && props.exportFormat != 'jpg' && props.exportFormat != 'pdf';
        }

        disabled.value = element.locked ||
            !!element.texture ||
            element.allowEdit ||
            exportFormatDisabled;

        model.shadowType = element.shadowType;
        model.shadowOffset = element.shadowOffset || 0;
        model.shadowAngle = element.shadowAngle || 0;
        model.shadowBlur = element.shadowBlur || 0;
        model.shadowColorOption = element.shadowColorOption;
        model.shadowColorLibraryId = element.shadowColorLibraryId;
        model.shadowColorsMap = new Map(element.shadowColorsMap);
    },
    { immediate: true }
);

const changeShadowType = (type: ShadowTypeEnum) => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    const newType = model.shadowType === type ? null : type;
    model.shadowType = newType;
    element.set('shadowType', newType);

    const isBlurred = newType === ShadowTypeEnum.Blurred;
    const isCleared = newType == null;

    // Sync model and element
    const sync = <K extends 'shadowOffset' | 'shadowAngle' | 'shadowBlur'>(
        prop: K,
        value: number
    ) => {
        element.set(prop, value);
        model[prop] = value;
    };

    if (isCleared) {
        // Clear values
        sync('shadowOffset', 0);
        sync('shadowAngle', 0);
        sync('shadowBlur', 0);
    } else {
        // Set default values
        if (model.shadowOffset === 0) sync('shadowOffset', defaultOffset);
        if (model.shadowAngle === 0) sync('shadowAngle', defaultAngle);

        // Set blur
        if (isBlurred && model.shadowBlur === 0) {
            sync('shadowBlur', defaultBlur);
        } else if (!isBlurred && model.shadowBlur !== 0) {
            sync('shadowBlur', 0);
        }
    }

    if (props.editMode) {
        ['shadowType', 'shadowOffset', 'shadowAngle', 'shadowBlur'].forEach(prop =>
            element.setEditedProp(prop)
        );
    }
    props.canvas.requestRenderAll();
    emit('shadow-change');
};

const changeShadowOffset = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowOffset', model.shadowOffset);

    if (props.editMode) {
        element.setEditedProp('shadowOffset');
    }
    props.canvas.requestRenderAll();
};

const changeShadowAngle = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowAngle', model.shadowAngle);

    if (props.editMode) {
        element.setEditedProp('shadowAngle');
    }
    props.canvas.requestRenderAll();
};

const changeShadowBlur = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowBlur', model.shadowBlur);

    if (props.editMode) {
        element.setEditedProp('shadowBlur');
    }
    props.canvas.requestRenderAll();
};

const onShadowColorSelected = async (value: ElementMapOption, option = 1) => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return; 
    const color = value as ColorObject;
    if (!color) return;

    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }

    element.shadowColorsMap.set(option, color);
    model.shadowColorsMap = new Map(element.shadowColorsMap);
    if (option == 1) {
        element.shadowColor = color;
    }

    loadColorIntoElement(option, undefined);
};

const previewColorOption = (color?: ElementMapOption) => {
    loadColorIntoElement(model.shadowColorOption || 1, color);
};

const removeColorOptionFromMap = (position: number) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;
  
    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }
    element.shadowColorsMap.delete(position);
    model.shadowColorsMap.delete(position);
    loadColorIntoElement(1, undefined);
    model.shadowColorsMap = new Map(model.shadowColorsMap);
};

const { hexToRgba } = useColor();
const loadColorIntoElement = (option: number, value?: ElementMapOption) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    const colorMap = element.shadowColorsMap;
    const color = (value as ColorObject) || colorMap.get(option);

    if (!color) return;

    const rgba = color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex;
    
    element.setShadowColor(rgba);
    element.shadowColorOption = model.shadowColorOption = option;

    props.canvas.requestRenderAll();
};

const onColorOptionsReordered = (
    options: KeyValueDto<number, ElementMapOption>[]
) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    const colorMap = new Map<number, FontColorOption>();
    options.forEach(({ value }, index) => {
        const position = index + 1;
        colorMap.set(position, value as FontColorOption);
    });
    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }
    element.shadowColorsMap = colorMap;
    model.shadowColorsMap = new Map(colorMap);
    loadColorIntoElement(1, undefined);
};

const pickColorLibrary = () => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    element.shadowColorOption= model.shadowColorOption = 1;
    emit('open-shadow-color-library-selector');
}


const removeColorLibrary = () => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    element.shadowColorLibraryId = undefined;
    if (props.editMode) {
        element.setEditedProp('shadowColorLibraryId');
    }
    loadColorIntoElement(1, undefined);

    model.shadowColorLibraryId = undefined;
};

</script>

<template>
    <div class="text-shadow-wrapper">
        <section class="shadow-section">
            <div class="shadow-grid-wrapper" role="list" v-if="!props.isPreview">
                <div
                    v-for="option in shadowOptions"
                    :key="option.type"
                    role="button"
                    tabindex="0"
                    class="shadow-thumb"
                    :class="{
                        selected: model.shadowType === option.type,
                        disabled: disabled
                    }"
                    @click="!disabled && changeShadowType(option.type)"
                >
                    <img
                        :src="option.img"
                        :alt="option.alt"
                        class="shadow-image"
                    />
                </div>
            </div>
        </section>
        <section class="shadow-section" v-if="model.shadowType != null">
            <h6>Color</h6>
            <div class="border-bottom" />
            <element-library-selector
                :disabled="disabled"
                :element-list="model.shadowColorsMap"
                :library-id="model.shadowColorLibraryId"
                :library-type="COLOR_TYPE"
                :selected-position="model.shadowColorOption"
                :cmyk="cmykMode"
                @pick-library="pickColorLibrary()"
                @remove-library="removeColorLibrary()"
                @option-selected="onShadowColorSelected"
                @remove-map-option="removeColorOptionFromMap($event)"
                @load-option="(option, value) => loadColorIntoElement(option, value)"
                @options-reordered="onColorOptionsReordered($event)"
                @preview-selection="previewColorOption($event)"
            />
        </section>
        <section class="shadow-section" v-if="model.shadowType != null && !props.isPreview">
            <h6>Settings</h6>
            <div class="border-bottom" />
                <div class="row align-items-center">
                    <div class="col-6">
                        <label>Offset: {{ model.shadowOffset }}%</label>
                        <c-slider
                            v-model="model.shadowOffset"
                            formatter="{value}%"
                            :max="100"
                            @input="changeShadowOffset"
                            :disabled="disabled"
                        />
                    </div>
                    <div class="col-6">
                        <label>Angle: {{ model.shadowAngle }}°</label>
                        <c-slider
                            v-model="model.shadowAngle"
                            formatter="{value}°"
                            :max="365"
                            @input="changeShadowAngle"
                            :disabled="disabled"
                        />
                    </div>
                </div>
                <div class="row align-items-center">
                    <div class="col-6" v-if="model.shadowType === BLURRED_SHADOW">
                        <label>Blur: {{ model.shadowBlur }}%</label>
                        <c-slider
                            v-model="model.shadowBlur"
                            formatter="{value}%"
                            :max="100"
                            @input="changeShadowBlur"
                            :disabled="disabled"
                        />
                    </div>
                </div>
        </section>
    </div>
</template>


<style lang="scss" scoped>
.text-shadow-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    .shadow-section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin-bottom: 0;
        }
    }
}

.shadow-grid-wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
}

.shadow-thumb.selected {
    border: 2px solid #7258E0;
    box-shadow: 0 2px 6px rgba(111,66,193,0.08);
}

.shadow-thumb {
    cursor: pointer;
    border: 1px solid #E6E6E6;
    border-radius: 6px;
    padding: 6px;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    transition: box-shadow 0.12s ease, border-color 0.12s ease, transform 0.06s ease;
    user-select: none;
    outline: none;
}

.shadow-thumb:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px #7258E0;
}

.shadow-thumb:focus-visible {
    box-shadow: 0 0 0 2px #7258E0;;
}

.shadow-image {
    background-color: #fff;
    max-width: 100%;
    object-fit: cover;
    padding: 0;
    display: block;
}

.shadow-thumb {
    cursor: pointer;
    opacity: 1;
    transition: opacity 0.2s ease;

    &.disabled {
        pointer-events: none;
        opacity: 0.4;
        cursor: not-allowed;
    }
}
</style>